import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Package, Truck, Mail, RefreshCw, CheckCircle2, Clock, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Admin() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [sendingTracking, setSendingTracking] = useState({});
  const [message, setMessage] = useState("");

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/orders/list`);
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleTrackingChange = (sessionId, field, value) => {
    setTrackingInputs(prev => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        [field]: value
      }
    }));
  };

  const sendTracking = async (sessionId) => {
    const tracking = trackingInputs[sessionId];
    if (!tracking?.tracking_number) {
      setMessage("Please enter a tracking number");
      return;
    }

    setSendingTracking(prev => ({ ...prev, [sessionId]: true }));
    setMessage("");

    try {
      const response = await fetch(`${API}/orders/send-tracking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          tracking_number: tracking.tracking_number,
          carrier: tracking.carrier || "USPS"
        })
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(`✅ ${data.message}`);
        fetchOrders(); // Refresh orders
      } else {
        setMessage(`❌ Error: ${data.detail}`);
      }
    } catch (error) {
      setMessage(`❌ Error: ${error.message}`);
    }

    setSendingTracking(prev => ({ ...prev, [sessionId]: false }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 pt-24 pb-20 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#17181B]">Order Management</h1>
            <p className="text-gray-600">View orders and send tracking numbers</p>
          </div>
          <Button 
            onClick={fetchOrders} 
            variant="outline"
            className="flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.includes('✅') ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#1167B1]" />
            <p className="mt-4 text-gray-600">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-20 text-center">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-xl font-semibold text-gray-600">No orders yet</h3>
              <p className="text-gray-500">Orders will appear here once customers make purchases</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <Card key={order.session_id} className="border-2 hover:border-[#1167B1] transition-all">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <Package className="w-5 h-5 text-[#1167B1]" />
                        {order.donor_name || 'Customer'}
                      </CardTitle>
                      <p className="text-sm text-gray-500">{order.donor_email}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={order.tracking_number ? 'bg-green-600' : 'bg-yellow-500'}>
                        {order.tracking_number ? 'Shipped' : 'Pending Shipment'}
                      </Badge>
                      <p className="text-2xl font-bold text-[#1167B1] mt-1">${order.amount?.toFixed(2)}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Order Details */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Order Info
                      </h4>
                      <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                        <p><strong>Date:</strong> {formatDate(order.created_at)}</p>
                        <p><strong>Session:</strong> {order.session_id?.substring(0, 20)}...</p>
                        {order.items && order.items.length > 0 && (
                          <div className="mt-2">
                            <strong>Items:</strong>
                            <ul className="list-disc list-inside ml-2 mt-1">
                              {order.items.map((item, idx) => (
                                <li key={idx} className="text-gray-600">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Shipping Address */}
                    <div>
                      <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Shipping Address
                      </h4>
                      {order.shipping_address ? (
                        <div className="bg-gray-50 p-3 rounded-lg text-sm">
                          <p><strong>{order.shipping_address.full_name}</strong></p>
                          <p>{order.shipping_address.address}</p>
                          <p>{order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</p>
                          <p>{order.shipping_address.country}</p>
                          {order.shipping_address.phone && (
                            <p className="mt-1"><strong>Phone:</strong> {order.shipping_address.phone}</p>
                          )}
                        </div>
                      ) : (
                        <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-500">
                          No shipping address provided
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tracking Section */}
                  <div className="mt-6 pt-4 border-t">
                    {order.tracking_number ? (
                      <div className="flex items-center gap-3 bg-green-50 p-4 rounded-lg">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="font-semibold text-green-800">Tracking Sent</p>
                          <p className="text-sm text-green-700">
                            {order.carrier || 'USPS'}: {order.tracking_number}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                          <Truck className="w-5 h-5" /> Send Tracking Number
                        </h4>
                        <div className="flex flex-wrap gap-3">
                          <div className="flex-1 min-w-[200px]">
                            <Label className="text-xs text-yellow-700">Tracking Number</Label>
                            <Input
                              placeholder="e.g., 9400111899223..."
                              value={trackingInputs[order.session_id]?.tracking_number || ''}
                              onChange={(e) => handleTrackingChange(order.session_id, 'tracking_number', e.target.value)}
                            />
                          </div>
                          <div className="w-32">
                            <Label className="text-xs text-yellow-700">Carrier</Label>
                            <select
                              className="w-full h-10 px-3 border rounded-md"
                              value={trackingInputs[order.session_id]?.carrier || 'USPS'}
                              onChange={(e) => handleTrackingChange(order.session_id, 'carrier', e.target.value)}
                            >
                              <option value="USPS">USPS</option>
                              <option value="UPS">UPS</option>
                              <option value="FedEx">FedEx</option>
                              <option value="DHL">DHL</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <Button
                              onClick={() => sendTracking(order.session_id)}
                              disabled={sendingTracking[order.session_id]}
                              className="bg-[#1167B1] hover:bg-[#0d5390]"
                            >
                              {sendingTracking[order.session_id] ? (
                                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Mail className="w-4 h-4 mr-2" />
                              )}
                              Send
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Admin;
